import { useMemo } from 'react';

import { parseRootPageString } from '../pageTreeSearchMacroParams.ts';

import { usePageTreeSearchQuery } from './usePageTreeSearchQuery.ts';
import { useSpaceValidationQuery } from './useSpaceValidationQuery.ts';

export type ResolveAncestorIdResult = {
	spaceKey: string;
	ancestorId: string;
	loading: boolean;
	errors: string[];
};

type Args = {
	rootPageString: string | undefined;
	defaultSpaceKey: string;
	contentId: string | undefined;
	contentType: string | undefined;
};

export const useResolveAncestorId = ({
	rootPageString,
	defaultSpaceKey,
	contentId,
	contentType,
}: Args): ResolveAncestorIdResult => {
	const parsed = useMemo(
		() =>
			rootPageString != null ? parseRootPageString(rootPageString, defaultSpaceKey) : undefined,
		[rootPageString, defaultSpaceKey],
	);

	const hasTitle = Boolean(parsed?.title);
	const hasExplicitSpace = parsed?.hasExplicitSpace ?? false;
	const shouldLookupPage = Boolean(parsed?.spaceKey) && hasTitle;
	// Space-only validation for "MYSPACE:" (explicit space, no title)
	const shouldValidateSpaceOnly = hasExplicitSpace && !hasTitle;

	// Combined query: resolves page, and validates space when explicitly provided
	const { data, error, loading } = usePageTreeSearchQuery(
		parsed?.spaceKey ?? '',
		parsed?.title ?? '',
		hasExplicitSpace,
		!shouldLookupPage,
	);

	// Space-only query: used when title is empty (e.g. "MYSPACE:")
	const {
		data: spaceData,
		error: spaceError,
		loading: spaceLoading,
	} = useSpaceValidationQuery(parsed?.spaceKey ?? '', !shouldValidateSpaceOnly);

	return useMemo<ResolveAncestorIdResult>(() => {
		// No rootPage parameter — fall back to the current page.
		// The legacy macro only allows this when the content is a Page (not blog, comment, etc.)
		if (parsed === undefined) {
			if (!contentId || contentType !== 'page') {
				return {
					spaceKey: defaultSpaceKey,
					ancestorId: '',
					loading: false,
					errors: ['invalidPageContext'],
				};
			}
			return { spaceKey: defaultSpaceKey, ancestorId: contentId, loading: false, errors: [] };
		}

		// Wait for in-flight queries
		if ((shouldLookupPage && loading) || (shouldValidateSpaceOnly && spaceLoading)) {
			return { spaceKey: parsed.spaceKey, ancestorId: '', loading: true, errors: [] };
		}

		// Collect errors independently, matching legacy behavior where both
		// space and page errors can appear simultaneously
		const errors: string[] = [];

		if (hasTitle) {
			// Combined query returned both space and content — check space from same response
			if (hasExplicitSpace && !data?.space) {
				errors.push('rootSpaceNotFound');
			}
			const resolvedId = data?.content?.nodes?.[0]?.id ?? null;
			if (error || !resolvedId) {
				errors.push('rootPageNotFound');
			} else {
				return { spaceKey: parsed.spaceKey, ancestorId: resolvedId, loading: false, errors };
			}
		} else if (hasExplicitSpace) {
			// Space-only validation (e.g. "MYSPACE:")
			if (spaceError || !spaceData?.space) {
				errors.push('rootSpaceNotFound');
			}
		}

		return { spaceKey: parsed.spaceKey, ancestorId: '', loading: false, errors };
	}, [
		parsed,
		hasTitle,
		hasExplicitSpace,
		shouldLookupPage,
		shouldValidateSpaceOnly,
		contentId,
		contentType,
		defaultSpaceKey,
		loading,
		error,
		data,
		spaceLoading,
		spaceError,
		spaceData,
	]);
};
